import React from 'react';

/**
 * Displays a package price with optional strikethrough + offer price.
 * Falls back to plain price when the package is not on offer.
 *
 * Props:
 *  - price: number (original)
 *  - offerEnabled: bool
 *  - offerPrice: number (new discounted price)
 *  - size: 'sm' | 'md' | 'lg'  (visual scale)
 *  - suffix: string (e.g. '/ Adult'; default provided)
 *  - align: 'start' | 'end' (text alignment for the block)
 *  - onDark: bool (inverts colours for dark backgrounds like the sticky booking bar)
 */
const SIZES = {
  sm: { main: 'text-base', old: 'text-[11px]', label: 'text-[10px]', badge: 'text-[9px]' },
  md: { main: 'text-lg',  old: 'text-xs',     label: 'text-xs',    badge: 'text-[10px]' },
  lg: { main: 'text-2xl', old: 'text-sm',     label: 'text-xs',    badge: 'text-[11px]' },
};

const PriceTag = ({
  price = 0,
  offerEnabled = false,
  offerPrice = 0,
  size = 'md',
  suffix = '/ Adult',
  align = 'start',
  onDark = false,
  showLabel = true,
  offerEndsIn = '',
}) => {
  const s = SIZES[size] || SIZES.md;
  const original = Number(price) || 0;
  const offer = Number(offerPrice) || 0;
  const isOnOffer = !!offerEnabled && offer > 0 && offer < original;
  const displayPrice = isOnOffer ? offer : original;

  const discount = isOnOffer && original > 0 ? Math.round(((original - offer) / original) * 100) : 0;

  const alignCls = align === 'end' ? 'items-end text-right' : 'items-start text-left';
  const labelCol = onDark ? 'text-white/80' : 'text-gray-500';
  const oldCol = onDark ? 'text-white/70' : 'text-gray-400';
  const mainCol = onDark ? 'text-white' : (isOnOffer ? 'text-rose-600' : 'text-[#0d64a8]');
  const suffixCol = onDark ? 'text-white/80' : 'text-gray-500';

  return (
    <div className={`flex flex-col ${alignCls}`} data-testid="price-tag">
      {showLabel && (
        <div className={`${s.label} ${labelCol}`}>Starting From:</div>
      )}
      {isOnOffer && (
        <div className="flex items-center gap-1.5 leading-none mt-0.5">
          <span className={`${s.old} ${oldCol} line-through`} data-testid="price-original">
            INR {Math.round(original).toLocaleString('en-IN')}
          </span>
          {discount > 0 && (
            <span className={`${s.badge} font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 leading-none`} data-testid="price-discount-badge">
              {discount}% OFF
            </span>
          )}
        </div>
      )}
      <div className={`${s.main} font-bold ${mainCol} leading-tight`} data-testid={isOnOffer ? 'price-offer' : 'price-normal'}>
        INR {Math.round(displayPrice).toLocaleString('en-IN')}
        {suffix && <span className={`text-xs font-normal ${suffixCol}`}> {suffix}</span>}
      </div>
      {isOnOffer && offerEndsIn && (
        <div
          className={`text-[10px] mt-1 leading-none ${onDark ? 'text-rose-200' : 'text-rose-600'} font-medium tabular-nums flex items-center gap-1`}
          data-testid="price-offer-ends-in"
        >
          <span className="inline-block w-1 h-1 rounded-full bg-rose-500 animate-pulse" />
          Offer ends in {offerEndsIn}
        </div>
      )}
    </div>
  );
};

export default PriceTag;
